import React, { useEffect, useState } from "react";
import { toast } from "react-toastify";
import { hasPermission } from "../../utils/helper";
import { PERMISSION } from "../../utils/constants";
import CourierListPage from "./courier-list-page";
import CourierTable from "./ui/courier-table";

// Mirrors core.courier.choices (int values).
const TASK_STATUS = { PENDING: 1, ENROUTE: 2, REACHED: 3, COMPLETED: 7 };
const TASK_TYPE = { CENTER_PICKUP: 1, HO_DELIVERY: 2 };
const TRIP_STATUS_ASSIGNED = 1;
const OUTCOMES = [
  { value: 1, label: "Collected" },
  { value: 2, label: "Zero Samples" },
  { value: 3, label: "Not Visited" }
];
const OUTCOME_COLLECTED = 1;

const CUSTODY_TYPES = [
  { value: 1, label: "Cash" },
  { value: 2, label: "Envelope" },
  { value: 3, label: "Other" }
];
const CUSTODY_CASH = 1;
const CUSTODY_STATUS_DISCREPANCY = 5;
const emptyCustody = {
  itemType: 1,
  amount: "",
  quantity: "",
  reference: "",
  handedOverBy: ""
};

const OperatorWorkflow = props => {
  const {
    getCourierTrips,
    trips = [],
    courierLoading,
    getCourierTripTasks,
    tripTasks = [],
    startCourierTrip,
    deliverCourierTrip,
    reachCourierTask,
    dispatchCourierTask,
    outcomeCourierTask,
    overrideGpsCourierTask,
    getCourierCustodyItems,
    createCourierCustodyItem,
    reconcileCourierCustodyItem,
    custodyItems = [],
    resetCourierState
  } = props;

  const [selectedTrip, setSelectedTrip] = useState(null);
  const [dispatchValue, setDispatchValue] = useState("");
  const [outcome, setOutcome] = useState(OUTCOME_COLLECTED);
  const [sampleCount, setSampleCount] = useState("");
  const [reason, setReason] = useState("");
  const [custodyTaskId, setCustodyTaskId] = useState(null);
  const [custodyForm, setCustodyForm] = useState(emptyCustody);

  const canOperate = hasPermission(PERMISSION.CAN_OPERATE_COURIER_TRIP);
  const canReconcile = hasPermission(PERMISSION.CAN_RECONCILE_COURIER_TRIP);
  const canOverrideGps = hasPermission(PERMISSION.CAN_OVERRIDE_COURIER_GPS);

  useEffect(() => {
    getCourierTrips({});
    return () => resetCourierState();
  }, []);

  const openTrip = trip => {
    setSelectedTrip(trip);
    getCourierTripTasks(trip.id);
  };

  const markReached = task => {
    if (!navigator.geolocation) {
      toast.error("Geolocation is not available on this device.");
      return;
    }
    navigator.geolocation.getCurrentPosition(
      pos => {
        reachCourierTask({
          id: task.id,
          tripId: selectedTrip.id,
          data: {
            latitude: pos.coords.latitude,
            longitude: pos.coords.longitude
          }
        });
      },
      () => toast.error("Could not read your location.")
    );
  };

  const saveDispatch = task => {
    if (!dispatchValue) return;
    dispatchCourierTask({
      id: task.id,
      tripId: selectedTrip.id,
      data: { dispatchId: dispatchValue }
    });
    setDispatchValue("");
  };

  const submitOutcome = task => {
    const data = { outcome: Number(outcome) };
    if (Number(outcome) === OUTCOME_COLLECTED) data.sampleCount = Number(sampleCount || 0);
    if (reason) data.reason = reason;
    outcomeCourierTask({ id: task.id, tripId: selectedTrip.id, data });
    setSampleCount("");
    setReason("");
    setOutcome(OUTCOME_COLLECTED);
  };

  const isActive = task =>
    task.status === TASK_STATUS.ENROUTE || task.status === TASK_STATUS.REACHED;

  const toggleCustody = task => {
    if (custodyTaskId === task.id) {
      setCustodyTaskId(null);
      return;
    }
    setCustodyTaskId(task.id);
    setCustodyForm(emptyCustody);
    getCourierCustodyItems(task.id);
  };

  const addCustody = task => {
    const data = {
      itemType: Number(custodyForm.itemType),
      reference: custodyForm.reference,
      handedOverBy: custodyForm.handedOverBy
    };
    if (Number(custodyForm.itemType) === CUSTODY_CASH) {
      data.amount = custodyForm.amount;
    }
    if (custodyForm.quantity) data.quantity = Number(custodyForm.quantity);
    createCourierCustodyItem({ taskId: task.id, data });
    setCustodyForm(emptyCustody);
  };

  const reconcileItem = item => {
    let data = {};
    if (item.itemType === CUSTODY_CASH) {
      const received = window.prompt(
        "Received amount:",
        item.amount != null ? String(item.amount) : ""
      );
      if (received === null) return;
      data.receivedAmount = received;
      if (Number(received) !== Number(item.amount)) {
        const why = window.prompt("Discrepancy reason:", "");
        if (why === null) return;
        data.discrepancyReason = why;
      }
    }
    reconcileCourierCustodyItem({
      id: item.id,
      taskId: custodyTaskId,
      data
    });
  };

  return (
    <CourierListPage
      title="My Workflow"
      count={trips.length}
      countLabel="trips"
      loading={courierLoading}
    >
      <div className="courier-workflow">
          <div className="courier-workflow-list">
            <h3 className="courier-subhead">My Trips</h3>
            <CourierTable>
              <thead>
                <tr>
                  <th>Code</th>
                  <th>Date</th>
                  <th>Status</th>
                  <th className="act">Actions</th>
                </tr>
              </thead>
              <tbody>
                {trips.map(t => (
                  <tr key={t.id}>
                    <td>{t.code}</td>
                    <td>{t.tripDate}</td>
                    <td>{t.statusDisplay}</td>
                    <td className="act">
                      <a
                        title="Open"
                        className="action icon-edit"
                        onClick={() => openTrip(t)}
                      />
                    </td>
                  </tr>
                ))}
                {!trips.length && !courierLoading && (
                  <tr>
                    <td colSpan={4}>No trips.</td>
                  </tr>
                )}
              </tbody>
            </CourierTable>
          </div>

          {selectedTrip && (
            <div className="courier-workflow-detail">
              <h3 className="courier-subhead">
                {selectedTrip.code} — {selectedTrip.statusDisplay}
              </h3>

              {canOperate && selectedTrip.status === TRIP_STATUS_ASSIGNED && (
                <div className="btns-holder">
                  <button
                    className="create-btn"
                    onClick={() => startCourierTrip(selectedTrip.id)}
                  >
                    Start Trip
                  </button>
                </div>
              )}

              {tripTasks.map(task => (
                <div key={task.id} className="operator-task">
                  <div className="operator-task-head">
                    #{task.sequence} {task.centerName || "Head Office"} —{" "}
                    {task.statusDisplay}
                    {task.radiusOk === false && (
                      <span className="courier-alert">
                        (GPS out of radius)
                      </span>
                    )}
                  </div>

                  {canOperate &&
                    task.taskType === TASK_TYPE.CENTER_PICKUP &&
                    isActive(task) && (
                      <div className="operator-task-body">
                        {task.status === TASK_STATUS.ENROUTE && (
                          <div className="btns-holder">
                            <button
                              className="create-btn"
                              onClick={() => markReached(task)}
                            >
                              Mark Reached
                            </button>
                          </div>
                        )}

                        <div className="courier-toolbar">
                          <input
                            placeholder="Dispatch ID"
                            value={dispatchValue}
                            onChange={e => setDispatchValue(e.target.value)}
                          />
                          <button
                            className="create-btn"
                            onClick={() => saveDispatch(task)}
                          >
                            Save Dispatch
                          </button>
                        </div>

                        {task.status === TASK_STATUS.REACHED && (
                          <div className="courier-toolbar">
                            <select
                              value={outcome}
                              onChange={e => setOutcome(e.target.value)}
                            >
                              {OUTCOMES.map(o => (
                                <option key={o.value} value={o.value}>
                                  {o.label}
                                </option>
                              ))}
                            </select>
                            {Number(outcome) === OUTCOME_COLLECTED && (
                              <input
                                type="number"
                                min="0"
                                placeholder="Samples"
                                value={sampleCount}
                                onChange={e => setSampleCount(e.target.value)}
                              />
                            )}
                            {Number(outcome) !== OUTCOME_COLLECTED && (
                              <input
                                placeholder="Reason"
                                value={reason}
                                onChange={e => setReason(e.target.value)}
                              />
                            )}
                            <button
                              className="create-btn"
                              onClick={() => submitOutcome(task)}
                            >
                              Submit Outcome
                            </button>
                          </div>
                        )}
                      </div>
                    )}

                  {task.taskType === TASK_TYPE.CENTER_PICKUP && (
                    <div className="operator-task-body">
                      <a
                        className="courier-link"
                        onClick={() => toggleCustody(task)}
                      >
                        {custodyTaskId === task.id ? "Hide Custody" : "Custody"}
                      </a>

                      {custodyTaskId === task.id && (
                        <div className="custody-box">
                          <CourierTable>
                            <thead>
                              <tr>
                                <th>Type</th>
                                <th>Amount</th>
                                <th>Qty</th>
                                <th>Ref</th>
                                <th>Status</th>
                                {canReconcile && <th className="act">Actions</th>}
                              </tr>
                            </thead>
                            <tbody>
                              {custodyItems.map(item => (
                                <tr key={item.id}>
                                  <td>{item.itemTypeDisplay}</td>
                                  <td>{item.amount || "-"}</td>
                                  <td>{item.quantity || "-"}</td>
                                  <td>{item.reference || "-"}</td>
                                  <td
                                    className={
                                      item.status === CUSTODY_STATUS_DISCREPANCY
                                        ? "courier-alert"
                                        : ""
                                    }
                                  >
                                    {item.statusDisplay}
                                  </td>
                                  {canReconcile && (
                                    <td className="act">
                                      <a
                                        title="Reconcile"
                                        className="action icon-sync"
                                        onClick={() => reconcileItem(item)}
                                      />
                                    </td>
                                  )}
                                </tr>
                              ))}
                              {!custodyItems.length && (
                                <tr>
                                  <td colSpan={canReconcile ? 6 : 5}>
                                    No custody items.
                                  </td>
                                </tr>
                              )}
                            </tbody>
                          </CourierTable>

                          {canOperate && (
                            <div className="courier-toolbar">
                              <select
                                value={custodyForm.itemType}
                                onChange={e =>
                                  setCustodyForm({
                                    ...custodyForm,
                                    itemType: e.target.value
                                  })
                                }
                              >
                                {CUSTODY_TYPES.map(t => (
                                  <option key={t.value} value={t.value}>
                                    {t.label}
                                  </option>
                                ))}
                              </select>
                              {Number(custodyForm.itemType) === CUSTODY_CASH && (
                                <input
                                  placeholder="Amount"
                                  value={custodyForm.amount}
                                  onChange={e =>
                                    setCustodyForm({
                                      ...custodyForm,
                                      amount: e.target.value
                                    })
                                  }
                                />
                              )}
                              <input
                                placeholder="Qty"
                                value={custodyForm.quantity}
                                onChange={e =>
                                  setCustodyForm({
                                    ...custodyForm,
                                    quantity: e.target.value
                                  })
                                }
                              />
                              <input
                                placeholder="Reference"
                                value={custodyForm.reference}
                                onChange={e =>
                                  setCustodyForm({
                                    ...custodyForm,
                                    reference: e.target.value
                                  })
                                }
                              />
                              <input
                                placeholder="Handed over by"
                                value={custodyForm.handedOverBy}
                                onChange={e =>
                                  setCustodyForm({
                                    ...custodyForm,
                                    handedOverBy: e.target.value
                                  })
                                }
                              />
                              <button
                                className="create-btn"
                                onClick={() => addCustody(task)}
                              >
                                Add Custody
                              </button>
                            </div>
                          )}
                        </div>
                      )}
                    </div>
                  )}

                  {canOverrideGps && task.radiusOk === false && (
                    <a
                      className="courier-link"
                      onClick={() =>
                        overrideGpsCourierTask({
                          id: task.id,
                          tripId: selectedTrip.id,
                          data: {}
                        })
                      }
                    >
                      Override GPS
                    </a>
                  )}

                  {canReconcile &&
                    task.taskType === TASK_TYPE.HO_DELIVERY &&
                    task.status !== TASK_STATUS.COMPLETED && (
                      <div className="btns-holder">
                        <button
                          className="create-btn"
                          onClick={() =>
                            deliverCourierTrip({
                              id: selectedTrip.id,
                              tripId: selectedTrip.id,
                              data: {}
                            })
                          }
                        >
                          Reconcile & Deliver
                        </button>
                      </div>
                    )}
                </div>
              ))}
              {!tripTasks.length && (
                <p className="courier-empty">No tasks for this trip.</p>
              )}
            </div>
          )}
      </div>
    </CourierListPage>
  );
};

export default OperatorWorkflow;
